import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { Layout } from "~/components/Layout";
import { Container } from "~/components/Container";
import { useState } from "react";
import { json, Link, useLoaderData } from "@remix-run/react";
import { eq } from "drizzle-orm";
import { db } from "~/database/db";
import { userTable } from "~/database/schema";
import { getCookieAuth } from "~/lib/.server/cookie";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { canAccessPaidPlanFeatures, isPaidPlanPubliclyAvailable } from "~/lib/.server/paid-plan-access";
import { FreePlanCard } from "~/components/FreePlanCard";
import { DeepDivePricingCard } from "~/components/DeepDivePricingCard";
import { createTopMeta } from "~/lib/meta";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const auth = await getCookieAuth(request);
  let user = null;
  if (auth) {
    user = await db.query.userTable.findFirst({
      where: eq(userTable.id, auth.userId),
    });
  }

  const isPaidPlanPublic = isPaidPlanPubliclyAvailable();
  const canAccessPaidPlan = auth ? canAccessPaidPlanFeatures(auth.userId) : false;

  return json({ user, isPaidPlanPublic, canAccessPaidPlan });
};

export const meta: MetaFunction = () => createTopMeta();

export default function Index() {
  const { user, isPaidPlanPublic, canAccessPaidPlan } = useLoaderData<typeof loader>();
  const showPaidPlan = canAccessPaidPlan || isPaidPlanPublic;
  const [billingInterval, setBillingInterval] = useState<"month" | "year">("year");

  return (
    <Layout user={user}>
      <Container>
        {/* hero start */}
        <div className="mt-10 md:mt-20 flex flex-col items-center justify-center text-center">

          <h1 className="text-5xl font-bold mb-4 flex flex-col items-center">
            <span>Rediscover Your Forgotten Raindrops</span>
          </h1>
          <div className="text-lg mt-3 mb-7 text-gray-500 max-w-2xl">
            Transform your bookmarked articles into daily discoveries. Get randomized summaries of your Raindrop.io collection delivered straight to your inbox.
          </div>

          <div className="mb-8">
            <Link
              to={'/signin'}
              className="bg-black text-white px-6 py-3 rounded-md inline-block hover:bg-gray-500 transition-colors w-[270px]"
            >
              Sign in with Raindrop.io
            </Link>
            <p className="text-md text-gray-500 mt-4">
              or{' '}
              <Link
                to="/demo"
                className="text-blue-600 hover:text-blue-700 hover:underline"
              >
                try the demo
              </Link>
            </p>
          </div>

          <a
            href="https://www.producthunt.com/products/dewdrop-2?embed=true&utm_source=badge-top-post-badge&utm_medium=badge&utm_campaign=badge-dewdrop-2"
            target="_blank"
            rel="noopener noreferrer"
            className=""
          >
            <img
              alt="Dewdrop - Turn your forgotten bookmarks into daily discoveries | Product Hunt"
              width="250"
              height="54"
              src="https://api.producthunt.com/widgets/embed-image/v1/top-post-badge.svg?post_id=1045835&theme=light&period=daily&t=1769481467447"
            />
          </a>


        </div>
        {/* hero end */}

        {/* features start */}
        <div className="mt-14 mb-20 bg-gray-50 py-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Dewdrop automatically curates your saved articles and creates randomized daily summaries
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="text-center">
              <CardHeader>
                <div className="mb-4 min-w-48 min-h-48">
                  <img
                    src="/lp_images/daily-randomized-bookmarks.png"
                    alt="Daily Summary Generation"
                    className="w-48 mx-auto rounded-xl shadow-md"
                  />
                </div>
                <CardTitle className="text-xl">Daily Randomized Bookmarks</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Every day, Dewdrop randomly selects articles from your Raindrop.io collection and curates randomized bookmarks to help you rediscover forgotten gems.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="mb-4 min-w-48 min-h-48">
                  <img
                    src="/lp_images/email-notifications.png"
                    alt="Email Notifications"
                    className="w-48 mx-auto rounded-lg shadow-md"
                  />
                </div>
                <CardTitle className="text-xl">Email Notifications</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Receive beautifully formatted curated bookmark notifications with excerpts from your selected articles. Never miss an opportunity to revisit valuable content.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
        {/* features end */}

        {/* how to get started start */}
        <div className="mt-20 mb-20">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Getting Started</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                All you need is to connect your Raindrop.io account - it&apos;s that simple!
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="text-center">
                <div className="mb-6 min-w-48 min-h-48">
                  <img
                    src="/lp_images/connect-your-account.png"
                    alt="Connect Raindrop.io Account"
                    className="w-48 max-w-xs mx-auto rounded-lg shadow-md"
                  />
                </div>
                <h3 className="text-xl font-semibold mb-3">1. Connect Your Account</h3>
                <p className="text-gray-600">
                  Sign in with your Raindrop.io account using OAuth. Your credentials are never stored on our servers.
                </p>
              </div>

              <div className="text-center">
                <div className="mb-6 min-w-48 min-h-48">
                  <img
                    src="/lp_images/automatic-processing.png"
                    alt="Automatic Summary Generation"
                    className="w-48 max-w-xs mx-auto rounded-lg shadow-md"
                  />
                </div>
                <h3 className="text-xl font-semibold mb-3">2. Automatic Processing</h3>
                <p className="text-gray-600">
                  Dewdrop analyzes your bookmarks and creates daily bookmark selections featuring randomly chosen articles from your collection.
                </p>
              </div>

              <div className="text-center">
                <div className="mb-6 min-w-48 min-h-48">
                  <img
                    src="/lp_images/daily-delivery.png"
                    alt="Email Delivery"
                    className="w-48 max-w-xs mx-auto rounded-lg shadow-md"
                  />
                </div>
                <h3 className="text-xl font-semibold mb-3">3. Daily Delivery</h3>
                <p className="text-gray-600">
                  Receive your randomized bookmark selection via email every day. Click through to revisit articles you may have forgotten.
                </p>
              </div>
            </div>
          </Container>
        </div>
        {/* how to get started end */}

        {/* demo section start */}
        <div className="mt-20 mb-20 bg-gray-50 py-16">
          <Container>
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">Try Dewdrop with Sample Data</h2>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Experience how Dewdrop works before connecting your account. Browse through a curated selection of popular bookmarks and see how daily discoveries are presented.
              </p>
              <Link
                to="/demo"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
              >
                Try Interactive Demo
              </Link>
            </div>
          </Container>
        </div>
        {/* demo section end */}

        {/* pricing start */}
        <div className="mt-20 mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Pricing</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Start with our free plan or unlock advanced features with Deep Dive
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <FreePlanCard
              action={
                <Link to="/signin" className="block">
                  <button className="w-full bg-gray-800 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200">
                    Get Started for Free
                  </button>
                </Link>
              }
            />

            {/* Deep Dive */}
            <DeepDivePricingCard
              showPaidPlan={showPaidPlan}
              billingInterval={billingInterval}
              onBillingIntervalChange={setBillingInterval}
              action={
                showPaidPlan ? (
                  <Link to="/pricing" className="block">
                    <button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200">
                      View Plan Details
                    </button>
                  </Link>
                ) : (
                  <button
                    className="w-full bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg cursor-not-allowed"
                    disabled
                  >
                    Coming Soon
                  </button>
                )
              }
            />
          </div>

          {/* Deep Dive をリリースするまでコメントアウト <div className="mt-8 text-center">
            <Link to="/pricing" className="text-blue-600 hover:text-blue-700 hover:underline font-medium">
              Compare plans in detail →
            </Link>
          </div> */}
        </div>
        {/* pricing end */}

        {/* faq start */}
        <div className="mt-20 mb-20 bg-gray-50 py-16">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Everything you need to know about Dewdrop
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-8">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold mb-3">How do I delete my account?</h3>
                <p className="text-gray-600">
                  You can delete your account at any time from your dashboard. Go to Settings → Account → Delete Account.
                  All your data will be permanently removed from our servers within 24 hours.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold mb-3">What data do you store?</h3>
                <p className="text-gray-600">
                  We only store your Raindrop.io user ID, email address, access tokens, and generated summaries.
                  We never store your Raindrop.io password or access your bookmarks directly.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold mb-3">How often are summaries generated?</h3>
                <p className="text-gray-600">
                  New bookmark selections are created once daily, typically in the morning.
                  The timing ensures you receive your daily dose of rediscovered content at the start of your day.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold mb-3">Can I customize which bookmarks are included?</h3>
                <p className="text-gray-600">
                  Currently, Dewdrop randomly selects from all your public bookmarks.
                  Customization features for specific collections and tags are planned for future updates.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold mb-3">Is my Raindrop.io data safe?</h3>
                <p className="text-gray-600">
                  Yes, we use OAuth 2.0 for secure authentication. We never store your credentials and only access
                  your bookmarks through Raindrop.io&apos;s official API with your explicit permission.
                </p>
              </div>
            </div>
          </Container>
        </div>
        {/* faq end */}

        {/* cta start */}
        <div className="mt-20 mb-20">
          <Container>
            <div className="text-center">
              <h2 className="text-4xl font-bold mb-4">Ready to Rediscover Your Bookmarks?</h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-600">
                Connect your Raindrop.io account and start receiving personalized daily summaries of your forgotten bookmarks.
                It&apos;s completely free and takes less than a minute to set up.
              </p>
              <p className="text-sm mb-4 text-gray-500">
                Want to see how it works first?{' '}
                <Link
                  to="/demo"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Try our interactive demo
                </Link>
              </p>
              <div className="space-y-4">
                <Link
                  to={'/signin'}
                  className="bg-black text-white px-6 py-3 rounded-md inline-block hover:bg-gray-500 transition-colors"
                >
                  Sign in with Raindrop.io
                </Link>
                <p className="text-sm text-gray-500">
                  No credit card required • Free forever • Secure OAuth integration
                </p>
              </div>
            </div>
          </Container>
        </div>
        {/* cta end */}
      </Container>
    </Layout>
  );
}